import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { mergeTournamentTeamImage } from '../_utils/shieldUtils';
import { API, apiErr, roundRobinPairs, toDateTimeLocalValue } from './setup/tournamentSetupHelpers';
import TournamentSetupTabPanel from './setup/TournamentSetupTabPanel';
import TournamentSetupGroupsTab from './setup/TournamentSetupGroupsTab';
import TournamentSetupPoolTab from './setup/TournamentSetupPoolTab';
import TournamentSetupGroupMatchesTab from './setup/TournamentSetupGroupMatchesTab';
import TournamentSetupBracketTab from './setup/TournamentSetupBracketTab';
import TeamLibraryDialog from './setup/TeamLibraryDialog';
import PublicMenuImageField from './setup/PublicMenuImageField';
import { useConfirm } from '../common/ConfirmDialog';

function slugSuggestionFromName(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export default function TournamentSetup() {
  const { tournamentId } = useParams();
  const confirm = useConfirm();
  const [tab, setTab] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState('');

  const [tournament, setTournament] = useState(null);
  const [access, setAccess] = useState(null);
  const [pubVisibility, setPubVisibility] = useState(0);
  const [pubSlug, setPubSlug] = useState('');
  const [pubShareCode, setPubShareCode] = useState('');
  const [pubMenuLeftUrl, setPubMenuLeftUrl] = useState('');
  const [pubMenuRightUrl, setPubMenuRightUrl] = useState('');
  // Datos del torneo (nombre, descripción, fecha). Independientes del objeto
  // `tournament` para que el usuario pueda editar sin pisar accidentalmente
  // el resto del estado y vea "guardado" claro al pulsar Guardar.
  const [metaName, setMetaName] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaDay, setMetaDay] = useState('');
  const [groups, setGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);

  const [newGroupName, setNewGroupName] = useState('');
  const [poolTeamName, setPoolTeamName] = useState('');
  const [poolTeamShield, setPoolTeamShield] = useState('');
  const [newTeamInGroupDraft, setNewTeamInGroupDraft] = useState({});
  const [shieldByGroup, setShieldByGroup] = useState({});
  const [addMenu, setAddMenu] = useState({ anchor: null, groupId: null });
  const [libraryDialog, setLibraryDialog] = useState({ open: false, onPick: null });

  const [scheduleGroupId, setScheduleGroupId] = useState('');
  const [roundRobinDouble, setRoundRobinDouble] = useState(false);
  const [groupScheduleStart, setGroupScheduleStart] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  const [groupMatchSpacingMinutes, setGroupMatchSpacingMinutes] = useState(120);
  const [groupMatches, setGroupMatches] = useState([]);
  const [manualLocalId, setManualLocalId] = useState('');
  const [manualForeignId, setManualForeignId] = useState('');
  const [manualDayHour, setManualDayHour] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return toDateTimeLocalValue(d);
  });
  const [manualFieldName, setManualFieldName] = useState('');

  const [groupMatchEditOpen, setGroupMatchEditOpen] = useState(false);
  const [groupMatchEditBase, setGroupMatchEditBase] = useState(null);
  const [gmLocalId, setGmLocalId] = useState('');
  const [gmForeignId, setGmForeignId] = useState('');
  const [gmDayHour, setGmDayHour] = useState('');
  const [gmFieldName, setGmFieldName] = useState('');

  const [bracketMatches, setBracketMatches] = useState([]);
  const [groupPositions, setGroupPositions] = useState([]);

  const refreshAll = useCallback(async () => {
    if (!tournamentId) return;
    setLoadError('');
    try {
      const [tRes, gRes, tmRes] = await Promise.all([
        axios.get(`${API}/api/tournaments/${tournamentId}`),
        axios.get(`${API}/api/tournamentgroups/${tournamentId}/teams/ordered`),
        axios.get(`${API}/api/tournamentteams/${tournamentId}/teams`),
      ]);
      setTournament(tRes.data);
      try {
        const ax = await axios.get(`${API}/api/tournaments/${tournamentId}/access`);
        setAccess(ax.data);
      } catch {
        setAccess(null);
      }
      const groupsArr = Array.isArray(gRes.data) ? gRes.data : [];
      const teamsArr = Array.isArray(tmRes.data) ? tmRes.data : [];
      setGroups(groupsArr);
      setTeams(teamsArr);

      let matchesArr = [];
      try {
        const mtRes = await axios.get(`${API}/api/tournamentmatches/${tournamentId}`);
        matchesArr = Array.isArray(mtRes.data) ? mtRes.data : [];
      } catch {
        matchesArr = [];
      }
      setGroupMatches(matchesArr);

      let br = [];
      try {
        const brRes = await axios.get(`${API}/api/tournamentmatches/${tournamentId}/bracketmatchs`);
        br = Array.isArray(brRes.data) ? brRes.data : [];
      } catch {
        br = [];
      }
      setBracketMatches(br);

      let positions = [];
      try {
        const posRes = await axios.get(`${API}/api/tournamentgroups/${tournamentId}/positions`);
        positions = Array.isArray(posRes.data) ? posRes.data : [];
      } catch {
        positions = [];
      }
      setGroupPositions(positions);

      let avList = [];
      try {
        const avRes = await axios.get(`${API}/api/tournamentteams/${tournamentId}/availableteams`);
        avList = Array.isArray(avRes.data) ? avRes.data : [];
      } catch {
        const inGroup = new Set();
        for (const g of groupsArr) {
          for (const gt of g.tournamentGroupTeams || []) inGroup.add(gt.teamId);
        }
        avList = teamsArr.filter((t) => !inGroup.has(t.id));
      }
      setAvailableTeams(avList);
    } catch (e) {
      setLoadError(apiErr(e));
    }
  }, [tournamentId]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const tabByHash = {
      grupos: 0,
      groups: 0,
      equipos: 1,
      pool: 1,
      teams: 1,
      calendario: 2,
      schedule: 2,
      eliminatoria: 3,
      bracket: 3,
    };

    const applyHash = () => {
      const key = (window.location.hash || "").replace(/^#/, "").toLowerCase();
      if (!key) return;
      const idx = tabByHash[key];
      if (typeof idx === "number") setTab(idx);
    };

    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  useEffect(() => {
    if (groups.length && !scheduleGroupId) {
      setScheduleGroupId(groups[0].id);
    }
  }, [groups, scheduleGroupId]);

  useEffect(() => {
    if (!tournament) return;
    const v = tournament.visibility ?? tournament.Visibility;
    setPubVisibility(typeof v === 'number' && !Number.isNaN(v) ? v : 0);
    setPubSlug(String(tournament.slug ?? tournament.Slug ?? ''));
    setPubShareCode(String(tournament.shareCode ?? tournament.ShareCode ?? ''));
    setPubMenuLeftUrl(String(tournament.publicMenuLeftImageUrl ?? tournament.PublicMenuLeftImageUrl ?? ''));
    setPubMenuRightUrl(String(tournament.publicMenuRightImageUrl ?? tournament.PublicMenuRightImageUrl ?? ''));

    setMetaName(String(tournament.name ?? tournament.Name ?? ''));
    setMetaDescription(String(tournament.description ?? tournament.Description ?? ''));
    const rawDay = tournament.day ?? tournament.Day;
    setMetaDay(
      typeof rawDay === 'string'
        ? rawDay
        : rawDay
          ? toDateTimeLocalValue(new Date(rawDay))
          : ''
    );
  }, [tournament]);

  const title = tournament?.name || 'Torneo';

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === scheduleGroupId),
    [groups, scheduleGroupId]
  );

  const teamIdsInSelectedGroup = useMemo(() => {
    if (!selectedGroup?.tournamentGroupTeams) return [];
    return selectedGroup.tournamentGroupTeams.map((x) => x.teamId);
  }, [selectedGroup]);

  const groupRoundRobinPreviewCount = useMemo(() => {
    if (teamIdsInSelectedGroup.length < 2) return 0;
    return roundRobinPairs(teamIdsInSelectedGroup, roundRobinDouble).length;
  }, [teamIdsInSelectedGroup, roundRobinDouble]);

  const sortedGroupMatches = useMemo(() => {
    return [...groupMatches].sort((a, b) => new Date(a.dayHour) - new Date(b.dayHour));
  }, [groupMatches]);

  const setDraftForGroup = (groupId, value) => {
    setNewTeamInGroupDraft((prev) => ({ ...prev, [groupId]: value }));
  };

  const setShieldForGroup = (groupId, shield) => {
    setShieldByGroup((prev) => ({ ...prev, [groupId]: shield }));
  };

  const closeAddMenu = () => setAddMenu({ anchor: null, groupId: null });

  const openGroupMatchEdit = (m) => {
    setGroupMatchEditBase(m);
    setGmLocalId(m.localTeamId || '');
    setGmForeignId(m.foreignTeamId || '');
    setGmDayHour(toDateTimeLocalValue(new Date(m.dayHour)));
    setGmFieldName(m.fieldName || '');
    setGroupMatchEditOpen(true);
  };

  const closeGroupMatchEdit = () => {
    setGroupMatchEditOpen(false);
    setGroupMatchEditBase(null);
  };

  const handleSaveGroupMatch = async () => {
    if (!groupMatchEditBase) return;
    if (!gmLocalId || !gmForeignId) {
      setLoadError('Elige local y visitante.');
      return;
    }
    if (gmLocalId === gmForeignId) {
      setLoadError('Local y visitante deben ser distintos.');
      return;
    }
    const when = new Date(gmDayHour);
    if (Number.isNaN(when.getTime())) {
      setLoadError('Fecha u hora no válida.');
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.put(`${API}/api/tournamentmatches/${groupMatchEditBase.id}`, {
        tournamentId,
        localTeamId: gmLocalId,
        foreignTeamId: gmForeignId,
        winnerTeamId: groupMatchEditBase.winnerTeamId ?? null,
        // wall-clock local, sin convertir a UTC (ver tournamentSetupHelpers)
        dayHour: gmDayHour,
        localGoals: groupMatchEditBase.localGoals ?? null,
        foreignGoals: groupMatchEditBase.foreignGoals ?? null,
        fieldName: gmFieldName.trim() || null,
        isActive: Boolean(groupMatchEditBase.isActive),
        completed: Boolean(groupMatchEditBase.completed),
      });
      setInfo('Partido de grupo actualizado.');
      closeGroupMatchEdit();
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    const name = newGroupName.trim();
    if (!name) return;
    setBusy(true);
    setInfo('');
    try {
      await axios.post(`${API}/api/tournamentgroups`, {
        tournamentId,
        name,
        description: '',
      });
      setNewGroupName('');
      setInfo('Grupo creado.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    setBusy(true);
    setLoadError('');
    try {
      const { data, status } = await axios.delete(`${API}/api/tournamentgroups/${groupId}`);
      if (data?.requiresConfirmation) {
        setBusy(false);
        if (!(await confirm({
          title: 'Eliminar grupo',
          message: data.warningMessage || `¿Eliminar el grupo "${groupName}"? Esta acción no se puede deshacer.`,
          confirmText: 'Eliminar',
          tone: 'danger',
        }))) return;
        setBusy(true);
        await axios.delete(`${API}/api/tournamentgroups/${groupId}?confirmed=true`);
      }
      setInfo('Grupo eliminado.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSetGroupColor = async (groupId, groupName, color) => {
    if (!groupId) return;
    setBusy(true);
    setLoadError('');
    try {
      await axios.put(`${API}/api/tournamentgroups/${groupId}`, {
        name: groupName,
        order: null,
        color: color || null,
      });
      setInfo(color ? `Color del grupo "${groupName}" guardado.` : `Color del grupo "${groupName}" eliminado.`);
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveTeamFromTournament = async (teamId, teamName) => {
    if (!teamId) return;
    const safeName = (teamName || '').trim() || 'este equipo';
    if (!(await confirm({
      title: 'Quitar equipo del torneo',
      message: `¿Quitar "${safeName}" del torneo? Solo se puede si no está asignado a un grupo ni participa en partidos.`,
      confirmText: 'Quitar',
      tone: 'danger',
    }))) return;
    setBusy(true);
    setInfo('');
    setLoadError('');
    try {
      await axios.delete(`${API}/api/tournamentteams/${teamId}`);
      setInfo(`«${safeName}» quitado del torneo.`);
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreatePoolTeam = async (e) => {
    e.preventDefault();
    const name = poolTeamName.trim();
    if (!name) return;
    setBusy(true);
    setInfo('');
    setLoadError('');
    try {
      const shield = (poolTeamShield || '').trim();
      const { data: created } = await axios.post(`${API}/api/tournamentteams`, {
        tournamentId,
        name,
        shield: shield || '',
        description: '',
      });
      if (shield) mergeTournamentTeamImage(tournamentId, created.id, shield);
      setPoolTeamName('');
      setPoolTeamShield('');
      setInfo('Equipo dado de alta. Aparece abajo en «listos para grupos»; asígnalo desde cada grupo.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateTeamInGroup = async (groupId) => {
    const name = (newTeamInGroupDraft[groupId] || '').trim();
    if (!name) return;
    const shield = (shieldByGroup[groupId] || '').trim();
    setBusy(true);
    setLoadError('');
    try {
      const { data: created } = await axios.post(`${API}/api/tournamentteams`, {
        tournamentId,
        name,
        shield: shield || '',
        description: '',
      });
      if (shield) mergeTournamentTeamImage(tournamentId, created.id, shield);
      await axios.post(`${API}/api/tournamentgroups/AddTeam`, {
        groupId,
        teamId: created.id,
      });
      setDraftForGroup(groupId, '');
      setShieldForGroup(groupId, '');
      setInfo(`Equipo «${name}» creado y añadido al grupo.`);
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const openTeamLibrary = useCallback((onPickFn) => {
    setLibraryDialog({ open: true, onPick: onPickFn });
  }, []);

  const closeTeamLibrary = useCallback(() => {
    setLibraryDialog({ open: false, onPick: null });
  }, []);

  /** Crea un equipo en la bolsa a partir de un item de la biblioteca personal. */
  const handleCreatePoolTeamFromLibrary = async ({ name, shield }) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setBusy(true);
    setInfo('');
    setLoadError('');
    try {
      const safeShield = (shield || '').trim();
      const { data: created } = await axios.post(`${API}/api/tournamentteams`, {
        tournamentId,
        name: trimmed,
        shield: safeShield,
        description: '',
      });
      if (safeShield) mergeTournamentTeamImage(tournamentId, created.id, safeShield);
      setInfo(`«${trimmed}» dado de alta desde tu biblioteca.`);
      await refreshAll();
      closeTeamLibrary();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  /** Crea un equipo desde la biblioteca y lo añade directamente a un grupo. */
  const handleCreateTeamInGroupFromLibrary = async (groupId, { name, shield }) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setBusy(true);
    setLoadError('');
    try {
      const safeShield = (shield || '').trim();
      const { data: created } = await axios.post(`${API}/api/tournamentteams`, {
        tournamentId,
        name: trimmed,
        shield: safeShield,
        description: '',
      });
      if (safeShield) mergeTournamentTeamImage(tournamentId, created.id, safeShield);
      await axios.post(`${API}/api/tournamentgroups/AddTeam`, {
        groupId,
        teamId: created.id,
      });
      setInfo(`«${trimmed}» añadido al grupo desde tu biblioteca.`);
      await refreshAll();
      closeTeamLibrary();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAddTeamToGroup = async (groupId, teamId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/tournamentgroups/AddTeam`, { groupId, teamId });
      setInfo('Equipo añadido al grupo.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveTeamFromGroup = async (teamId) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/api/tournamentgroups/RemoveTeam/${teamId}`);
      setInfo('Equipo quitado del grupo.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAddManualGroupMatch = async (e) => {
    e.preventDefault();
    if (!manualLocalId || !manualForeignId) {
      setLoadError('Elige equipo local y visitante.');
      return;
    }
    if (manualLocalId === manualForeignId) {
      setLoadError('Local y visitante deben ser distintos.');
      return;
    }
    const when = new Date(manualDayHour);
    if (Number.isNaN(when.getTime())) {
      setLoadError('Fecha u hora no válida.');
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.post(`${API}/api/tournamentmatches`, {
        tournamentId,
        localTeamId: manualLocalId,
        foreignTeamId: manualForeignId,
        // wall-clock local, sin convertir a UTC (ver tournamentSetupHelpers)
        dayHour: manualDayHour,
        fieldName: manualFieldName.trim() || null,
      });
      setInfo('Partido de grupo añadido al calendario.');
      setManualForeignId('');
      setManualFieldName('');
      // No auto-incrementamos la fecha: dejamos el último valor introducido para
      // facilitar crear varios partidos a la misma hora en distintos campos.
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteGroupMatch = async (matchId) => {
    setBusy(true);
    setLoadError('');
    try {
      const { data } = await axios.delete(`${API}/api/tournamentmatches/${matchId}`);
      if (data?.requiresConfirmation) {
        setBusy(false);
        if (!(await confirm({
          title: 'Eliminar partido',
          message: data.warningMessage || '¿Eliminar este partido del calendario de grupo?',
          confirmText: 'Eliminar',
          tone: 'danger',
        }))) return;
        setBusy(true);
        await axios.delete(`${API}/api/tournamentmatches/${matchId}?confirmed=true`);
      }
      setInfo('Partido eliminado.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateGroupRoundRobin = async () => {
    if (teamIdsInSelectedGroup.length < 2) {
      setLoadError('Hacen falta al menos 2 equipos en el grupo.');
      return;
    }
    if (
      !(await confirm({
        title: 'Generar calendario de grupo',
        message: `Se crearán ${groupRoundRobinPreviewCount} partidos de grupo en este torneo.`,
        confirmText: 'Generar',
      }))
    ) {
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    const pairs = roundRobinPairs(teamIdsInSelectedGroup, roundRobinDouble);
    let t = new Date(groupScheduleStart);
    try {
      for (const [localTeamId, foreignTeamId] of pairs) {
        await axios.post(`${API}/api/tournamentmatches`, {
          tournamentId,
          localTeamId,
          foreignTeamId,
          // wall-clock local, sin convertir a UTC
          dayHour: toDateTimeLocalValue(t),
          fieldName: null,
        });
        t = new Date(t.getTime() + groupMatchSpacingMinutes * 60000);
      }
      setInfo(`Listo: ${pairs.length} partidos de grupo creados. Revisa «Ver partidos».`);
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const onFileTooLargeShield = () => setLoadError('El escudo supera 1,5 MB. Prueba otra imagen.');

  const handleSaveMeta = async () => {
    if (!tournamentId || !tournament) return;
    const cleanName = metaName.trim();
    if (!cleanName) {
      setLoadError('El nombre del torneo no puede estar vacío.');
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      // Send name/description/day plus the current visibility/slug/shareCode so the
      // single PUT preserves the publishing config the user may have set earlier.
      const dayVal = metaDay && metaDay.trim()
        ? metaDay
        : toDateTimeLocalValue(new Date());
      await axios.put(`${API}/api/tournaments/${tournamentId}`, {
        name: cleanName,
        description: metaDescription ?? '',
        day: dayVal,
        visibility: pubVisibility,
        slug: pubVisibility === 2 ? pubSlug.trim() || null : null,
        shareCode: pubVisibility === 1 ? pubShareCode.trim() || null : null,
      });
      setInfo('Datos del torneo guardados.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSavePublishing = async () => {
    if (!tournamentId || !tournament) return;
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      // wall-clock local: si rawDay ya viene como string lo dejamos; si es Date,
      // lo formateamos a "YYYY-MM-DDTHH:mm" para no convertir a UTC (toISOString).
      const rawDay = tournament.day ?? tournament.Day;
      const dayVal =
        typeof rawDay === 'string'
          ? rawDay
          : rawDay
            ? toDateTimeLocalValue(new Date(rawDay))
            : toDateTimeLocalValue(new Date());
      await axios.put(`${API}/api/tournaments/${tournamentId}`, {
        name: tournament.name || '',
        description: tournament.description ?? '',
        day: dayVal,
        visibility: pubVisibility,
        slug: pubVisibility === 2 ? pubSlug.trim() || null : null,
        shareCode: pubVisibility === 1 ? pubShareCode.trim() || null : null,
      });
      setInfo(
        'Visibilidad guardada. En la portada solo aparecen torneos públicos y no finalizados.'
      );
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSavePublicMenuImages = async () => {
    if (!tournamentId) return;
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.put(`${API}/api/tournaments/${tournamentId}/public-menu-images`, {
        leftImageUrl: pubMenuLeftUrl.trim() || null,
        rightImageUrl: pubMenuRightUrl.trim() || null,
      });
      setInfo('Imágenes del menú público guardadas.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const canManagePublishing = Boolean(access?.canManage);

  return (
    <Container maxWidth="lg" className="py-6 px-3 sm:px-4">
      <Button
        component={RouterLink}
        to={`/tournamentactions/${tournamentId}`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
      >
        Volver al torneo
      </Button>

      <Paper
        elevation={0}
        className="rounded-3xl border border-arena/15 shadow-card overflow-hidden"
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          maxWidth: 640,
          mx: 'auto',
          bgcolor: 'rgba(255,255,255,0.97)',
        }}
      >
        <div className="h-1.5 -mx-4 -mt-4 mb-5 sm:-mx-8 sm:-mt-8 bg-gradient-to-r from-gold via-gold-bright to-arena-bright rounded-t-3xl" />
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: '#0f3531',
            fontSize: { xs: '1.35rem', sm: '1.5rem' },
            mb: 0.5,
          }}
        >
          Configurar · {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 2.5 }}>
          Crea equipos con escudo y déjalos en la <strong>bolsa</strong> (sin grupo); luego, en cada grupo, elige
          «Elegir equipo…» para colocarlos. Orden típico: grupos → bolsa → calendario de grupo → eliminatoria.
        </Typography>

        {/* Datos del torneo (editar nombre, descripción y fecha). */}
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid rgba(15,53,49,0.12)',
            bgcolor: 'rgba(15,107,92,0.04)',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f3531', mb: 1.25 }}>
            Datos del torneo
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Nombre"
              value={metaName}
              onChange={(e) => setMetaName(e.target.value)}
              disabled={busy}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              label="Descripción"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              disabled={busy}
              helperText="Aparece en la pantalla pública del torneo (hub)."
            />
            <TextField
              size="small"
              fullWidth
              type="datetime-local"
              label="Fecha y hora"
              value={metaDay}
              onChange={(e) => setMetaDay(e.target.value)}
              disabled={busy}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              type="button"
              variant="contained"
              onClick={handleSaveMeta}
              disabled={busy || !metaName.trim()}
              sx={{
                alignSelf: 'flex-start',
                bgcolor: '#0f6b5c',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#0d5a4f' },
              }}
            >
              Guardar datos
            </Button>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Button
            size="small"
            component={RouterLink}
            to={`/viewgroups/${tournamentId}`}
            variant="outlined"
            sx={{ borderColor: 'rgba(15,53,49,0.25)', color: '#0f3531', textTransform: 'none', fontWeight: 600 }}
          >
            Ver grupos
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to={`/matches/${tournamentId}`}
            variant="outlined"
            sx={{ borderColor: 'rgba(15,53,49,0.25)', color: '#0f3531', textTransform: 'none', fontWeight: 600 }}
          >
            Ver partidos
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to={`/tournamentactions/${tournamentId}/orden-grupos`}
            variant="outlined"
            sx={{ borderColor: 'rgba(201,162,39,0.45)', color: '#8f6f10', textTransform: 'none', fontWeight: 700 }}
          >
            Desempate manual
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to={`/tournamentactions/${tournamentId}/premium-media`}
            variant="outlined"
            sx={{ borderColor: 'rgba(15,107,92,0.35)', color: '#0f6b5c', textTransform: 'none', fontWeight: 700 }}
          >
            Estudio premium
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to={`/tournamentactions/${tournamentId}/mesa`}
            onClick={() => sessionStorage.setItem(`mesaTournamentName_${tournamentId}`, title)}
            variant="contained"
            sx={{
              bgcolor: '#0f6b5c',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#0d5a4f' },
            }}
          >
            Mesa (marcador)
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to={`/viewbracket/${tournamentId}`}
            variant="outlined"
            sx={{ borderColor: 'rgba(15,53,49,0.25)', color: '#0f3531', textTransform: 'none', fontWeight: 600 }}
          >
            Ver fase final
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to={`/tournamentactions/${tournamentId}/colaboradores`}
            variant="outlined"
            startIcon={<PersonAddIcon sx={{ fontSize: 18 }} />}
            sx={{ borderColor: 'rgba(15,53,49,0.25)', color: '#0f3531', textTransform: 'none', fontWeight: 600 }}
          >
            Colaboradores
          </Button>
        </Stack>

        {canManagePublishing && (
          <Paper
            elevation={0}
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(15,53,49,0.12)',
              bgcolor: 'rgba(15,107,92,0.04)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531', mb: 0.5 }}>
              Portada y público
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
              Cuando el torneo esté listo, puedes mostrarlo en la página de inicio como{' '}
              <strong>público</strong> (necesitas un slug único). La portada solo lista torneos{' '}
              <strong>no finalizados</strong>. Si lo dejas privado, solo lo ven quienes gestionan o
              tienen acceso según la app.
            </Typography>
            {tournament?.isFinished ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Este torneo está marcado como finalizado: no aparecerá en el listado público de la
                portada aunque sea público.
              </Alert>
            ) : null}
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="pub-vis-label">Visibilidad</InputLabel>
                <Select
                  labelId="pub-vis-label"
                  label="Visibilidad"
                  value={pubVisibility}
                  onChange={(e) => setPubVisibility(Number(e.target.value))}
                  disabled={busy}
                >
                  <MenuItem value={0}>Privado (solo organización / quien tenga acceso)</MenuItem>
                  <MenuItem value={1}>Solo enlace (código para compartir)</MenuItem>
                  <MenuItem value={2}>Público en la portada</MenuItem>
                </Select>
              </FormControl>
              {pubVisibility === 2 && (
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Slug de URL (único)"
                    placeholder="ej. copa-verano-2026"
                    value={pubSlug}
                    onChange={(e) => setPubSlug(e.target.value)}
                    disabled={busy}
                    helperText="Letras minúsculas, números y guiones. Se usará en la API pública."
                  />
                  <Button
                    size="small"
                    variant="text"
                    disabled={busy}
                    onClick={() => setPubSlug(slugSuggestionFromName(tournament?.name || ''))}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                  >
                    Generar slug desde el nombre
                  </Button>
                </Stack>
              )}
              {pubVisibility === 1 && (
                <TextField
                  size="small"
                  fullWidth
                  label="Código para compartir"
                  value={pubShareCode}
                  onChange={(e) => setPubShareCode(e.target.value)}
                  disabled={busy}
                  helperText="Quien tenga el enlace con este código podrá ver el torneo (API pública)."
                />
              )}
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f3531', mt: 1 }}>
                Menú público del torneo
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                Imágenes para la pantalla previa que ven los aficionados al abrir el menú del torneo: una a cada lado del
                nombre. Puedes subir un archivo desde tu equipo (recomendado) o pegar una URL pública (avanzado). Si las
                dejas vacías, se usan la imagen del torneo (si existe), el logo de la organización o unos valores por defecto.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <PublicMenuImageField
                    label="Imagen izquierda"
                    value={pubMenuLeftUrl}
                    onChange={setPubMenuLeftUrl}
                    disabled={busy}
                    onError={setLoadError}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <PublicMenuImageField
                    label="Imagen derecha"
                    value={pubMenuRightUrl}
                    onChange={setPubMenuRightUrl}
                    disabled={busy}
                    onError={setLoadError}
                  />
                </Box>
              </Stack>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={handleSavePublicMenuImages}
                sx={{
                  alignSelf: 'flex-start',
                  borderColor: 'rgba(15,107,92,0.45)',
                  color: '#0f6b5c',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Guardar imágenes del menú público
              </Button>
              <Button
                variant="contained"
                disabled={busy}
                onClick={handleSavePublishing}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: '#0f6b5c',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#0d5a4f' },
                }}
              >
                Guardar visibilidad
              </Button>
            </Stack>
          </Paper>
        )}

        {loadError && (
          <Alert severity="error" sx={{ my: 2 }} onClose={() => setLoadError('')}>
            {loadError}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ my: 2 }} onClose={() => setInfo('')}>
            {info}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            borderBottom: '1px solid',
            borderColor: 'rgba(15,53,49,0.12)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: 44,
              color: 'text.secondary',
            },
            '& .Mui-selected': { color: '#0f3531' },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: '#c9a227' },
          }}
        >
          <Tab label="Grupos y equipos" />
          <Tab label="Solo equipos" />
          <Tab label="Calendario grupo" />
          <Tab label="Eliminatoria" />
        </Tabs>

        <TournamentSetupTabPanel value={tab} index={0}>
          <TournamentSetupGroupsTab
            availableTeams={availableTeams}
            teams={teams}
            groups={groups}
            busy={busy}
            poolTeamName={poolTeamName}
            setPoolTeamName={setPoolTeamName}
            poolTeamShield={poolTeamShield}
            setPoolTeamShield={setPoolTeamShield}
            newGroupName={newGroupName}
            setNewGroupName={setNewGroupName}
            newTeamInGroupDraft={newTeamInGroupDraft}
            shieldByGroup={shieldByGroup}
            addMenu={addMenu}
            setAddMenu={setAddMenu}
            closeAddMenu={closeAddMenu}
            setDraftForGroup={setDraftForGroup}
            setShieldForGroup={setShieldForGroup}
            handleCreatePoolTeam={handleCreatePoolTeam}
            handleCreateGroup={handleCreateGroup}
            handleDeleteGroup={handleDeleteGroup}
            handleCreateTeamInGroup={handleCreateTeamInGroup}
            handleAddTeamToGroup={handleAddTeamToGroup}
            handleRemoveTeamFromGroup={handleRemoveTeamFromGroup}
            handleRemoveTeamFromTournament={handleRemoveTeamFromTournament}
            handleSetGroupColor={handleSetGroupColor}
            onFileTooLargeShield={onFileTooLargeShield}
            openTeamLibrary={openTeamLibrary}
            onPickFromLibraryForPool={handleCreatePoolTeamFromLibrary}
            onPickFromLibraryForGroup={handleCreateTeamInGroupFromLibrary}
          />
        </TournamentSetupTabPanel>

        <TournamentSetupTabPanel value={tab} index={1}>
          <TournamentSetupPoolTab
            teams={teams}
            availableTeams={availableTeams}
            busy={busy}
            poolTeamName={poolTeamName}
            setPoolTeamName={setPoolTeamName}
            poolTeamShield={poolTeamShield}
            setPoolTeamShield={setPoolTeamShield}
            handleCreatePoolTeam={handleCreatePoolTeam}
            handleRemoveTeamFromTournament={handleRemoveTeamFromTournament}
            onFileTooLargeShield={onFileTooLargeShield}
            openTeamLibrary={openTeamLibrary}
            onPickFromLibraryForPool={handleCreatePoolTeamFromLibrary}
          />
        </TournamentSetupTabPanel>

        <TournamentSetupTabPanel value={tab} index={2}>
          <TournamentSetupGroupMatchesTab
            teams={teams}
            groups={groups}
            busy={busy}
            sortedGroupMatches={sortedGroupMatches}
            scheduleGroupId={scheduleGroupId}
            setScheduleGroupId={setScheduleGroupId}
            roundRobinDouble={roundRobinDouble}
            setRoundRobinDouble={setRoundRobinDouble}
            groupScheduleStart={groupScheduleStart}
            setGroupScheduleStart={setGroupScheduleStart}
            groupMatchSpacingMinutes={groupMatchSpacingMinutes}
            setGroupMatchSpacingMinutes={setGroupMatchSpacingMinutes}
            teamIdsInSelectedGroup={teamIdsInSelectedGroup}
            groupRoundRobinPreviewCount={groupRoundRobinPreviewCount}
            manualLocalId={manualLocalId}
            setManualLocalId={setManualLocalId}
            manualForeignId={manualForeignId}
            setManualForeignId={setManualForeignId}
            manualDayHour={manualDayHour}
            setManualDayHour={setManualDayHour}
            manualFieldName={manualFieldName}
            setManualFieldName={setManualFieldName}
            handleAddManualGroupMatch={handleAddManualGroupMatch}
            handleDeleteGroupMatch={handleDeleteGroupMatch}
            handleGenerateGroupRoundRobin={handleGenerateGroupRoundRobin}
            onEditGroupMatch={openGroupMatchEdit}
            groupMatchDialog={{
              open: groupMatchEditOpen,
              onClose: closeGroupMatchEdit,
              onSave: handleSaveGroupMatch,
              busy,
              base: groupMatchEditBase,
              localId: gmLocalId,
              setLocalId: setGmLocalId,
              foreignId: gmForeignId,
              setForeignId: setGmForeignId,
              dayHour: gmDayHour,
              setDayHour: setGmDayHour,
              fieldName: gmFieldName,
              setFieldName: setGmFieldName,
            }}
          />
        </TournamentSetupTabPanel>

        <TournamentSetupTabPanel value={tab} index={3}>
          <TournamentSetupBracketTab
            tournamentId={tournamentId}
            teams={teams}
            groupPositions={groupPositions}
            bracketMatches={bracketMatches}
            refreshAll={refreshAll}
            setLoadError={setLoadError}
            setInfo={setInfo}
            busy={busy}
            setBusy={setBusy}
          />
        </TournamentSetupTabPanel>
      </Paper>

      <TeamLibraryDialog
        open={libraryDialog.open}
        onClose={closeTeamLibrary}
        onPick={(item) => libraryDialog.onPick && libraryDialog.onPick(item)}
        existingTeamNames={teams.map((t) => (t.name || '').toLowerCase().trim())}
        busy={busy}
      />
    </Container>
  );
}
